import { memo, useCallback, useEffect, useState } from 'react';
import type React from 'react';

import { ChevronDownIcon, PencilIcon } from 'lucide-react';

import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';
import { Textarea } from '@/shadcn/textarea';

import type { WidgetEditorBaseProps } from '@/components/ui/widget/views/text/text.view.type';

type Props = WidgetEditorBaseProps<string> & {
  delay?: number;
};

export const CommentEditor = memo(
  ({
    value,
    onChange,
    placeholder = 'Aa',
    delay = 250,
    header,
    disabled = false,
    identifier
  }: Props) => {
    // focus state
    const [inputStr, setInputStr] = useState(value);

    const updateInputStr = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInputStr(e.target.value);
      },
      []
    );

    // change the text displayed if the data provided changes
    useEffect(
      () => {
        setInputStr(value);
      },
      [value] // Only re-call effect if dataStr changes
    );

    useEffect(() => {
      if (inputStr === value) return;

      const handler = setTimeout(() => {
        onChange(inputStr || '');
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [inputStr]);

    if (disabled && !value) return null;

    const isInBatchEdit = !!header;

    if (isInBatchEdit) {
      return (
        <div className="flex flex-col gap-2">
          <Label
            data-cy={`comment-editor-${identifier}-label`}
            htmlFor={header}
          >
            {header}
          </Label>
          <Textarea
            className="w-full"
            data-cy={`comment-editor-${identifier}-textarea`}
            disabled={disabled}
            id={header}
            onChange={updateInputStr}
            placeholder={placeholder}
            rows={6}
            value={inputStr}
          />
        </div>
      );
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            className="flex size-full cursor-text flex-row items-center justify-between px-3 text-left hover:bg-accent"
            data-cy={`comment-editor-${identifier}-button`}
            id={identifier}
            type="button"
          >
            <div
              className={cn(
                'grow truncate text-foreground text-xs',
                (!inputStr || disabled) && 'text-muted-foreground'
              )}
              data-cy={`comment-editor-${identifier}-button-text`}
            >
              {inputStr || placeholder}
            </div>
            <div
              className="text-muted-foreground"
              data-cy={`comment-editor-${identifier}-button-icon`}
            >
              {disabled ? (
                <ChevronDownIcon size={16} />
              ) : (
                <PencilIcon size={16} />
              )}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="p-0">
          <Textarea
            data-cy={`comment-editor-${identifier}-textarea`}
            disabled={disabled}
            onChange={updateInputStr}
            placeholder={placeholder}
            value={inputStr}
          />
        </PopoverContent>
      </Popover>
    );
  }
);
